import { httpClient } from "./client";
import { getCsrfToken } from "../utils/csrf";
// Generated DTOs (ADR-005) are the single source of truth. The presigned-ticket
// response now carries `fields` (the S3 POST policy fields), `method`, and
// `public_url` — the device-upload flow needs all three — so it is the generated
// MediaUploadResponse, re-exported here under the same name for existing callers.
import type {
  MediaUploadResponse,
  PhotoUploadRequest,
} from "../types/generated";

export type { MediaUploadResponse, PhotoUploadRequest };

export const mediaApi = {
  /**
   * Get a size-bounded pre-signed S3 POST ticket.
   */
  getUploadUrl: (
    category: string,
    contentType: string,
    contentLength: number,
  ) =>
    httpClient.post<MediaUploadResponse>("/media/upload-url", {
      category,
      content_type: contentType,
      content_length: contentLength,
    }),

  getTournamentPosterUploadUrl: (tournamentId: string, contentType: string) =>
    httpClient.post<MediaUploadResponse>(
      `/media/tournaments/${tournamentId}/poster?content_type=${encodeURIComponent(contentType)}`,
      {},
    ),

  /**
   * Request a pre-signed POST ticket for a player's POST PHOTO (the composer's
   * device upload). POSTs the `PhotoUploadRequest` body — `content_type` +
   * `content_length` ONLY; `owner_id` / `category` are derived from the token
   * server-side. Returns the `MediaUploadResponse` ticket: the `upload_url` to
   * multipart-POST the file to, the presigned `fields`, and the permanent
   * `public_url` to persist in the post's `media_urls`. A disallowed type or an
   * over-cap size is a 422 at the request boundary (surfaced as an ApiError).
   */
  requestPhotoUpload: (body: PhotoUploadRequest) =>
    httpClient.post<MediaUploadResponse>("/media/photo-upload-url", body),
};

export interface UploadedMediaFile {
  publicUrl: string;
  objectKey: string;
}

/**
 * Upload a file through an already-issued media ticket.
 *
 * This is the single transport switch for every browser media flow: configured
 * object stores receive anonymous multipart form data, while the exact
 * same-origin preview endpoint receives the raw file with its session, CSRF
 * token, and signed preview ticket.
 */
export async function uploadFileWithTicket(
  file: File,
  ticket: MediaUploadResponse,
): Promise<UploadedMediaFile> {
  if (ticket.method !== "POST") {
    throw new Error(`Unsupported upload method: ${ticket.method}`);
  }
  if (!ticket.fields || Object.keys(ticket.fields).length === 0) {
    throw new Error("Upload ticket is missing signed form fields");
  }

  const uploadTarget = new URL(ticket.upload_url, window.location.origin);
  const usesAuthenticatedPreviewTransport =
    uploadTarget.origin === window.location.origin &&
    uploadTarget.pathname === "/bff/api/media/preview-upload";
  const previewCsrfToken = usesAuthenticatedPreviewTransport
    ? getCsrfToken()
    : null;
  if (usesAuthenticatedPreviewTransport && !previewCsrfToken) {
    throw new Error("Missing CSRF token for preview upload");
  }

  let uploadBody: BodyInit;
  let uploadHeaders: Record<string, string> | undefined;
  if (usesAuthenticatedPreviewTransport) {
    const previewTicket = ticket.fields.ticket;
    if (!previewTicket) {
      throw new Error("Preview upload ticket is missing its signature");
    }
    uploadBody = file;
    uploadHeaders = {
      "Content-Type": file.type,
      "X-CSRF-Token": previewCsrfToken as string,
      "X-Preview-Media-Ticket": previewTicket,
    };
  } else {
    const form = new FormData();
    for (const [name, value] of Object.entries(ticket.fields)) {
      form.append(name, value);
    }
    // S3 evaluates the policy fields before the payload; keep the file last.
    form.append("file", file);
    uploadBody = form;
  }

  const response = await fetch(ticket.upload_url, {
    method: "POST",
    body: uploadBody,
    credentials: usesAuthenticatedPreviewTransport ? "include" : "omit",
    ...(uploadHeaders ? { headers: uploadHeaders } : {}),
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  return {
    publicUrl: new URL(
      ticket.public_url || ticket.cdn_url,
      window.location.origin,
    ).toString(),
    objectKey: ticket.object_key,
  };
}

/**
 * Upload a self-owned file using the backend's size-bounded signed POST ticket.
 * Configured object stores receive multipart data without session credentials.
 * The preview-only backend transport instead receives the raw file with the
 * same-origin session, CSRF token, and signed preview ticket.
 */
export async function uploadFile(
  file: File,
  category: string,
): Promise<string> {
  const ticket = await mediaApi.getUploadUrl(category, file.type, file.size);
  return (await uploadFileWithTicket(file, ticket)).publicUrl;
}

export async function uploadTournamentPoster(
  file: File,
  tournamentId: string,
): Promise<string> {
  const { upload_url, cdn_url } = await mediaApi.getTournamentPosterUploadUrl(
    tournamentId,
    file.type,
  );

  const response = await fetch(upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  return cdn_url;
}
